import { IScheduledJob } from "@kuika/kuika-cl-model";
import * as React from "react";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { KuikaNameModal } from "../../ui-kit/kuika-name-modal/kuika-name-modal";

/**
 * Asks for the new job's name, and is the ONE place the job-name rule lives.
 *
 * Before this there were two, and they disagreed in a way that mattered: the create hero
 * accepted `^[a-zA-Z0-9 ]+$` while the editor's rename required a leading letter, so a job
 * created as "24h Sync" could never be renamed afterwards. The stricter rule wins rather
 * than the looser one — unusually, because this name is not just a label. The scheduler
 * generator writes each job to `Backend/SchedulerJobs/{job.Name}.cs` and uses it as the
 * generated function name (spaces become underscores), so a digit-leading name emits a C#
 * identifier that will not compile. Accepting one was a latent codegen fault, not a
 * capability.
 */

/** Start with a letter; letters, digits and spaces after that. Minimum two characters. */
export const SCHEDULED_JOB_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9 ]+$/;
export const SCHEDULED_JOB_NAME_MESSAGE: string =
  "Job name must start with a letter and can only contain letters, numbers and spaces.";

/** The confirm says what it is doing in its own words: a job is created, not saved. */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

export interface IScheduledJobNameModalProps {
  visible: boolean;
  /** Heading copy, so "Blank job" and "Use template" can each say what they are doing. */
  title: string;
  /** One line under the heading saying what happens next. */
  subtitle?: string;
  /** Glyph from `public/images/common/`, so the two entry points don't look alike. */
  iconName?: string;
  confirmLabel: string;
  /**
   * Where the create stands. The ✓ is held on the confirm before the new job's editor replaces
   * the page, and a failed create leaves the sheet open on "Try again".
   */
  status: KuikaButtonStatus;
  /** Prefilled name — a template's seed. Re-applied every time the modal opens. */
  initialValue?: string;
  /** Every job in the project — the uniqueness check runs against all of them. */
  jobs: IScheduledJob[];
  onCancel: () => void;
  onSubmit: (name: string) => void;
}

export class ScheduledJobNameModal extends React.PureComponent<IScheduledJobNameModalProps> {
  /**
   * Case-insensitive, and deliberately NOT locale-aware: `toLowerCase` is kept exactly as
   * the pre-refactor validator had it, so which duplicates get rejected does not shift for
   * Turkish-named projects.
   */
  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const jobs: IScheduledJob[] = this.props.jobs ?? [];
    if (jobs.length === 0) return;
    if (jobs.some((job: IScheduledJob): boolean => job.name.toLowerCase() === value.toLowerCase())) {
      throw new Error("This Scheduled Job Name is used before.");
    }
  };

  render() {
    return (
      <KuikaNameModal
        visible={this.props.visible}
        title={this.props.title}
        subtitle={this.props.subtitle}
        iconName={this.props.iconName ?? "clock"}
        confirmLabel={this.props.confirmLabel}
        status={this.props.status}
        statusLabels={CREATE_STATUS_LABELS}
        initialValue={this.props.initialValue}
        fieldName="scheduledJobName"
        placeholder="Enter job name"
        rules={[
          { required: true, message: "Please enter a name." },
          { pattern: SCHEDULED_JOB_NAME_PATTERN, message: SCHEDULED_JOB_NAME_MESSAGE },
          { validator: this.existingNameValidator }
        ]}
        // The id the create form has always emitted, so the E2E suite still finds the field.
        inputTestId="scheduledJobsNameInput"
        confirmTestId="scheduledJobsCreateButton"
        // Scope hook: `createButton` is not unique across the designer, and the confirm
        // button lives in the modal footer rather than inside the form.
        className="kuika-scheduled-job-name-modal"
        onCancel={this.props.onCancel}
        onSubmit={this.props.onSubmit}
      />
    );
  }
}
